import { KeyboardDoubleArrowLeft } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'

import libbySrc from '~/assets/images/about/libby.webp'
import { EChatbotTheme } from '~/features/settings/models'
import useIdentifyViewportWidth from '~/features/ui/hooks/useIdentifyViewportWidth'
import EElement from '~/features/ui/models'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  isChatting: boolean
  hideToggleChat: boolean
  toggleChatHandler: () => void
}

function Header({ isChatting, hideToggleChat, toggleChatHandler }: TProps) {
  const theme = useTheme()
  const { isExtraSmallScreen, isSmallScreen } = useIdentifyViewportWidth()
  const [chatbotLayout, chatbotAgent] = store((state) => [
    state.chatbot.layout,
    state.chatbot.agent,
  ])

  const isChatbotAgentDefined = !chatbotAgent || !chatbotAgent.businessName

  return (
    <Box
      id={EElement.chatbotHeader}
      sx={{
        paddingInline: { xxxs: isChatting ? 0 : 2, sm: 2 },
        width: '100%',
        top: 0,
        ...((isSmallScreen || isExtraSmallScreen) && isChatting
          ? { position: 'fixed' }
          : {}),
      }}
    >
      <Paper
        sx={{
          elevation: isChatting ? 3 : 0,
          mt: { xxxs: isChatting ? 0 : 2, sm: 2 },
          paddingBlock: isChatting ? 1 : 2,
          borderRadius: { xxxs: isChatting ? 0 : 4, sm: 4 },
          height: 'fit-content',
          minHeight: rem(80),
        }}
      >
        {isChatbotAgentDefined ? (
          <Stack
            gap={isChatting ? 1 : 2}
            sx={{
              alignItems: 'flex-start',
              position: 'relative',
              paddingBlock: 1,
              paddingInline: 2,
              flexDirection: 'column',
              gap: rem(3),
            }}
          >
            {isChatting && !hideToggleChat && (
              <Skeleton variant="rectangular" width={80} height={21} />
            )}
            <Stack
              sx={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isChatting ? 2 : 3,
                marginTop: { sm: isChatting ? rem(-18) : 0 },
              }}
            >
              <Skeleton
                variant="circular"
                sx={{
                  width: { xxxs: 32, sm: 40, md: 64 },
                  height: { xxxs: 32, sm: 40, md: 64 },
                }}
              />
              <Stack
                sx={{
                  alignItems: 'center',
                }}
              >
                <Skeleton variant="text" width={120} height={30} />
                <Skeleton variant="text" width={100} height={20} />
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack
            gap={isChatting ? 1 : 2}
            sx={{
              alignItems: 'flex-start',
              position: 'relative',
              paddingBlock: 1,
              paddingInline: 2,
              flexDirection: 'column',
              gap: rem(3),
            }}
          >
            {isChatting && !hideToggleChat && (
              <Button
                variant="tertiary1"
                startIcon={<KeyboardDoubleArrowLeft />}
                sx={{
                  height: rem(21),
                  '&.MuiButtonBase-root': {
                    paddingInline: rem(2),
                  },

                  '& svg': {
                    color: theme.palette.common.black,
                    fill: theme.palette.common.black,
                  },
                }}
                onClick={toggleChatHandler}
              >
                Back
              </Button>
            )}

            <Stack
              sx={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isChatting ? 2 : 3,
                marginTop: { sm: isChatting ? rem(-18) : 0 },
              }}
            >
              <Avatar
                src={chatbotAgent.chatbotImgURL ?? libbySrc}
                sx={{
                  width: {
                    xxxs: isChatting ? 32 : 50,
                    sm: isChatting ? 50 : 76,
                  },
                  height: {
                    xxxs: isChatting ? 32 : 50,
                    sm: isChatting ? 50 : 76,
                  },
                  transition: 'all 1s ease-in-out',
                  boxShadow: `
                  0 0 0 ${rem(3)} ${theme.palette.common.white},
                  0 0 0 ${rem(6)} ${chatbotLayout.options[EChatbotTheme.linkView][chatbotLayout.selection[EChatbotTheme.linkView]].accentColor} `,
                  borderRadius: '50%',
                }}
              />

              <Stack
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography
                  color={
                    chatbotLayout.options[EChatbotTheme.linkView][
                      chatbotLayout.selection[EChatbotTheme.linkView]
                    ].secondaryTextColor
                  }
                  sx={{
                    fontSize: {
                      xxxs: isChatting ? rem(14) : rem(18),
                      sm: isChatting ? rem(18) : rem(24),
                    },
                    fontWeight: theme.typography.fontWeightMedium,
                    textAlign: 'center',
                  }}
                >
                  {chatbotAgent.businessName}
                </Typography>
                <Typography
                  color={
                    chatbotLayout.options[EChatbotTheme.linkView][
                      chatbotLayout.selection[EChatbotTheme.linkView]
                    ].secondaryTextColor
                  }
                  sx={{
                    fontSize: {
                      xxxs: isChatting ? rem(12) : rem(16),
                      sm: isChatting ? rem(14) : rem(16),
                    },
                    textAlign: 'center',
                  }}
                >
                  {chatbotAgent.service}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  )
}

export default Header
